/**
 * Guards for values interpolated into a `<style>` block.
 *
 * A print stylesheet is assembled from operator-editable configuration and
 * injected with `dangerouslySetInnerHTML`. The values are *meant* to be
 * numbers, but they arrive as untyped JSON from the settings API, so a string
 * carrying `}</style><img onerror=…>` would be written verbatim into the
 * document (CWE-79). These helpers make that impossible by construction:
 * whatever comes in, only a number, a colour or a CSS identifier comes out.
 */

/**
 * Coerce a value destined for a CSS length to a finite number. Anything else —
 * a non-numeric string, `NaN`, `Infinity`, `null`, an object — becomes
 * `fallback`, so no caller-supplied text can reach the stylesheet.
 *
 * `null`, `undefined` and `''` are rejected rather than run through `Number`,
 * which would turn all three into a silent `0` and render `0mm` where the
 * caller asked for a default.
 */
export function cssNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : fallback;
  }

  if (typeof value !== 'string' || value.trim() === '') {
    return fallback;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

/** `#abc` / `#aabbcc` / `#aabbccdd`, a bare CSS keyword, or a `var(--token)` reference. */
const SAFE_CSS_COLOR =
  /^(?:#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})|[a-z]+|var\(--[a-z0-9-]+\))$/i;

/**
 * Accept a colour only if it matches the strict allowlist above. Anything else
 * — including any value containing a quote, brace, semicolon or angle bracket —
 * is replaced by `fallback`.
 */
export function cssColor(value: unknown, fallback = 'transparent'): string {
  return typeof value === 'string' && SAFE_CSS_COLOR.test(value.trim())
    ? value.trim()
    : fallback;
}

/**
 * Reduce a value to the characters that are legal in a CSS identifier /
 * attribute-selector value. Everything else is dropped rather than escaped, so
 * there is nothing left that could terminate the declaration or the element.
 */
export function cssIdent(value: unknown, fallback = 'x'): string {
  const cleaned = String(value ?? '').replace(/[^a-zA-Z0-9_-]/g, '');
  return cleaned.length > 0 ? cleaned : fallback;
}
